import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MessageSquare, X } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { Pagination } from '@/components/Pagination'
import { AwaitingReplyBadge, FeedbackStatusBadge } from '@/components/feedback/FeedbackStatusBadge'
import { FeedbackButton } from '@/components/feedback/FeedbackButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/useAuth'
import { useFeedbackReports } from '@/hooks/useFeedback'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { FeedbackScope, FeedbackStatus } from '@/types/feedback'

const STATUSES: FeedbackStatus[] = ['received', 'in_progress', 'resolved', 'not_planned']

export function Feedback() {
  const { t } = useTranslation('feedback')
  const { user } = useAuth()
  const isPlatformAdmin = !!user?.is_platform_admin
  const [scope, setScope] = useState<FeedbackScope>('org')
  const [status, setStatus] = useState<FeedbackStatus | 'all'>('all')
  const [organization, setOrganization] = useState<{ id: string; name: string } | null>(null)
  const [page, setPage] = useState(1)

  const showOrganization = scope === 'all'
  const { data, isLoading, isError } = useFeedbackReports({
    scope,
    status: status === 'all' ? undefined : status,
    organizationId: showOrganization ? organization?.id : undefined,
    page,
  })

  const scopes: FeedbackScope[] = isPlatformAdmin ? ['org', 'mine', 'all'] : ['org', 'mine']

  const changeScope = (next: FeedbackScope) => {
    setScope(next)
    setOrganization(null)
    setPage(1)
  }

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t('list.title')}</h1>
          <p className="text-muted-foreground">{t('list.subtitle')}</p>
        </div>
        <FeedbackButton variant="button" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {scopes.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  variant={scope === value ? 'default' : 'outline'}
                  aria-pressed={scope === value}
                  onClick={() => changeScope(value)}
                >
                  {t(`list.scopes.${value}`)}
                </Button>
              ))}
              {showOrganization && organization && (
                <span className="flex items-center gap-1 rounded-full border px-3 text-sm">
                  {t('list.organizationFilter', { name: organization.name })}
                  <button
                    type="button"
                    aria-label={t('list.clearOrganization')}
                    className="rounded p-0.5 hover:bg-muted"
                    onClick={() => {
                      setOrganization(null)
                      setPage(1)
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value as FeedbackStatus | 'all')
                setPage(1)
              }}
            >
              <SelectTrigger className="w-48" aria-label={t('list.statusFilter')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('list.allStatuses')}</SelectItem>
                {STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`statuses.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">{t('list.loadError')}</p>
          ) : data && data.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('list.columns.type')}</TableHead>
                    <TableHead>{t('list.columns.title')}</TableHead>
                    <TableHead>{t('list.columns.author')}</TableHead>
                    {showOrganization && <TableHead>{t('list.columns.organization')}</TableHead>}
                    <TableHead>{t('list.columns.date')}</TableHead>
                    <TableHead>{t('list.columns.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((report) => (
                    <TableRow
                      key={report.id}
                      className={cn(report.awaiting_reply && 'bg-amber-50 hover:bg-amber-100')}
                    >
                      <TableCell>{t(`types.${report.type}`)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/feedback/${report.id}`} className="font-medium hover:underline">
                            {report.title}
                          </Link>
                          {report.awaiting_reply && <AwaitingReplyBadge />}
                        </div>
                      </TableCell>
                      <TableCell>{report.author_name}</TableCell>
                      {showOrganization && (
                        <TableCell>
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={() => {
                              setOrganization({
                                id: report.organization_id,
                                name: report.organization_name,
                              })
                              setPage(1)
                            }}
                          >
                            {report.organization_name}
                          </button>
                        </TableCell>
                      )}
                      <TableCell>{formatDate(report.created_at)}</TableCell>
                      <TableCell>
                        <FeedbackStatusBadge status={report.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} totalPages={data.pages} onPageChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon={MessageSquare}
              title={t('list.emptyTitle')}
              description={t('list.emptyDescription')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
